import { select } from 'd3-selection';
import styleContants from './timebreaks.module.scss';
import { TimeSeriesDisableRange } from 'utils/timeseries/TimeSeriesDisableRange';

export type BreaksRenderType = 'line' | 'hide' | 'default';

/* eslint-disable @typescript-eslint/no-explicit-any */

export function haveBreaksChanged(
    prevBreaks: TimeSeriesDisableRange[],
    nextBreaks: TimeSeriesDisableRange[]
) {
    if (prevBreaks.length !== nextBreaks.length) {
        return true;
    }
    const breaksLength = prevBreaks.length;
    if (breaksLength > 0) {
        for (let i = 0; i < breaksLength; i++) {
            if (
                prevBreaks[i].from !== nextBreaks[i].from ||
                prevBreaks[i].to !== nextBreaks[i].to
            ) {
                return true;
            }
        }
    }
    return false;
}

export function getVirtualBreaks(
    width: number,
    startTime: number,
    endTime: number,
    breaks: TimeSeriesDisableRange[]
): TimeSeriesDisableRange[] {
    let totalBreaksTime = 0;
    for (const b of breaks) {
        totalBreaksTime += b.to - b.from;
    }
    const totalTime = endTime - startTime - totalBreaksTime;
    return breaks.map((b) => {
        let adjustedTo = b.to - (totalTime * 5) / width;
        if (adjustedTo < b.from) {
            adjustedTo = b.to;
        }
        return {
            from: b.from,
            to: adjustedTo,
        };
    });
}

export function fallsWithinBreak(
    time: number,
    breaks: TimeSeriesDisableRange[],
    breakType: BreaksRenderType
) {
    if (breakType !== 'default') {
        // None or hidden...
        return false;
    }
    for (const b of breaks) {
        if (time > b.from && time < b.to) {
            return true;
        }
    }
    return false;
}

export function renderTimeBreaks(
    node: any,
    isDomainValid: boolean,
    timeBreaks: TimeSeriesDisableRange[],
    xScale: any
) {
    renderBreakRectangle(node, isDomainValid, timeBreaks, xScale);
    renderBreakLine(node, isDomainValid, timeBreaks, xScale, 'end');
    renderBreakLine(node, isDomainValid, timeBreaks, xScale, 'start');
}

export const timeBreakRectWidth = 5;
export function renderBreakRectangle(
    node: any,
    isDomainValid: boolean,
    timeBreaks: TimeSeriesDisableRange[],
    xScale: any
) {
    const rectFillColour = styleContants.rectFillColour;

    const r = select(node)
        .select('.plot-area')
        .select('svg')
        .selectAll('rect.timebreak-rect')
        .data(timeBreaks.map((b) => b.from));

    r.enter()
        .append('rect')
        .attr('class', 'timebreak-rect')
        .attr('fill', rectFillColour)
        .merge(r as any)
        .attr('x', (t: number) => (isDomainValid ? xScale(t) : 0))
        .attr('y', 0)
        .attr('width', `${timeBreakRectWidth}px`)
        .attr('height', '100%');

    r.exit().remove();
}

function renderBreakLine(
    node: any,
    isDomainValid: boolean,
    timeBreaks: TimeSeriesDisableRange[],
    xScale: any,
    lineType: 'start' | 'end'
) {
    const className = 'timebreak-line-' + lineType;
    const xPadding = lineType === 'end' ? 5 : 0;

    const l = select(node)
        .select('.plot-area')
        .select('svg')
        .selectAll('line.' + className)
        .data(timeBreaks.map((b) => b.from));

    l.enter()
        .append('line')
        .attr('class', 'timebreak-line ' + className)
        .merge(l as any)
        .attr('x1', (t: number) => (isDomainValid ? xScale(t) + xPadding : 0))
        .attr('x2', (t: number) => (isDomainValid ? xScale(t) + xPadding : 0))
        .attr('y1', 0)
        .attr('y2', '100%');

    l.exit().remove();
}
